// Epoch 53: Outreach Credits Management Page
// Full credit management interface for usage-based pricing users
// Date: October 28, 2025

import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { useNavigate } from 'react-router-dom';

interface CreditBalanceData {
  balance: number;
  lifetime_purchased: number;
  lifetime_used: number;
  pricing_plan: string;
  recent_activity: {
    purchases: PackagePurchase[];
    usage: UsageRecord[];
    adjustments: Adjustment[];
  };
}

interface PackagePurchase {
  id: string;
  package_size: number;
  price_paid: number;
  currency: string;
  purchased_at: string;
  notes: string | null;
}

interface UsageRecord {
  id: string;
  outreaches_used: number;
  balance_before: number;
  balance_after: number;
  campaign_id: string | null;
  campaigns: { name: string } | null;
  recorded_at: string;
  notes: string | null;
}

interface Adjustment {
  id: string;
  delta_credits: number;
  reason: string;
  balance_before: number;
  balance_after: number;
  created_at: string;
  notes: string | null;
}

const CREDIT_PACKAGES = [
  {
    size: 500,
    price: 200,
    pricePerCredit: 0.40,
    popular: false,
    savings: null,
  },
  {
    size: 1000,
    price: 380,
    pricePerCredit: 0.38,
    popular: true,
    savings: 5,
  },
  {
    size: 2000,
    price: 744,
    pricePerCredit: 0.37,
    popular: false,
    savings: 7,
  },
  {
    size: 5000,
    price: 1800,
    pricePerCredit: 0.36,
    popular: false,
    savings: 10,
  },
];

const Credits: React.FC = () => {
  const navigate = useNavigate();
  const [balanceData, setBalanceData] = useState<CreditBalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'purchase' | 'usage' | 'purchases'>('purchase');

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/login');
          return;
        }

        // Check if user is on usage-based pricing
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('pricing_plan, role')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          console.error('Error fetching user data:', userError);
          setError('Failed to fetch user data');
          return;
        }

        // Redirect legacy flat_rate users who don't use the credits system
        // free_user (null plan) and platform/usage_based users all pass through
        if (userData.pricing_plan === 'flat_rate_legacy') {
          navigate('/dashboard');
          return;
        }

        // Fetch balance from Edge Function
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-outreach-balance`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch balance: ${response.statusText}`);
        }

        const data = await response.json();
        setBalanceData(data);
      } catch (error) {
        console.error('Error fetching credit balance:', error);
        setError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [navigate]);

  const handlePurchase = async (packageSize: number) => {
    try {
      setPurchasing(packageSize);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Call purchase Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/purchase-outreach-package`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            package_size: packageSize,
            success_url: `${window.location.origin}/credits?purchase=success`,
            cancel_url: `${window.location.origin}/credits?purchase=cancelled`,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      alert(error instanceof Error ? error.message : 'Failed to start purchase process');
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !balanceData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Credits</h3>
          <p className="text-red-700">{error || 'Failed to load credit information'}</p>
        </div>
      </div>
    );
  }

  const { balance, lifetime_purchased, lifetime_used, recent_activity } = balanceData;
  const usagePercentage = lifetime_purchased > 0
    ? Math.round((lifetime_used / lifetime_purchased) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Outreach Credits</h1>
        <p className="text-lg text-gray-600 mt-2">Manage your outreach credit balance and purchase history</p>
      </div>

      {/* Balance Summary */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl shadow-lg p-8 mb-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-purple-100 text-sm font-medium mb-2">Current Balance</p>
            <p className="text-5xl font-bold">{balance.toLocaleString()}</p>
            <p className="text-purple-100 text-sm mt-2">credits available</p>
          </div>
          <div className="text-6xl opacity-20">💳</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-purple-500">
          <div>
            <p className="text-purple-100 text-sm">Lifetime Purchased</p>
            <p className="text-2xl font-semibold">{lifetime_purchased.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-100 text-sm">Lifetime Used</p>
            <p className="text-2xl font-semibold">{lifetime_used.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-100 text-sm">Usage Rate</p>
            <p className="text-2xl font-semibold">{usagePercentage}%</p>
          </div>
        </div>

        {/* Low Balance Warning */}
        {balance < 100 && balance > 0 && (
          <div className="mt-6 bg-yellow-500 bg-opacity-20 border border-yellow-300 rounded-lg p-4">
            <p className="text-yellow-100 font-medium">⚠️ Low balance! Consider purchasing more credits below.</p>
          </div>
        )}

        {balance === 0 && (
          <div className="mt-6 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg p-4">
            <p className="text-red-100 font-medium">❌ No credits remaining! Purchase credits below to continue.</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('purchase')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'purchase'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Purchase Credits
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'usage'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Usage History
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'purchases'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Purchase History
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'purchase' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose a Credit Package</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CREDIT_PACKAGES.map((pkg) => (
              <div
                key={pkg.size}
                className={`relative bg-white rounded-xl shadow-sm border-2 p-6 hover:shadow-md transition-shadow ${
                  pkg.popular ? 'border-purple-600' : 'border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-gray-900 mb-2">
                    {pkg.size.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-sm">credits</p>
                </div>

                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-purple-600">${pkg.price}</p>
                  <p className="text-gray-500 text-sm mt-1">
                    ${pkg.pricePerCredit.toFixed(2)} per credit
                  </p>
                </div>

                {pkg.savings && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
                      SAVE {pkg.savings}%
                    </span>
                  </div>
                )}

                <button
                  onClick={() => handlePurchase(pkg.size)}
                  disabled={purchasing !== null}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    pkg.popular
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  } ${purchasing === pkg.size ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {purchasing === pkg.size ? 'Processing...' : 'Purchase Now'}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 About Outreach Credits</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Each credit equals one outreach (connection request, message reply, etc.)</li>
              <li>• Credits never expire</li>
              <li>• Secure payment via Stripe</li>
              <li>• Credits are added instantly after payment</li>
              <li>• Need more credits? Contact us for volume pricing</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'usage' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Usage History</h2>
          {recent_activity.usage.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Outreaches
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance Change
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recent_activity.usage.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(record.recorded_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {record.campaigns?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.outreaches_used.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="text-red-600 font-medium">
                          {record.balance_before.toLocaleString()} → {record.balance_after.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {record.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Usage History</h3>
              <p className="text-gray-600">
                Your credit usage will appear here once you start using outreach credits.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'purchases' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Purchase History</h2>
          {recent_activity.purchases.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recent_activity.purchases.map((purchase) => (
                    <tr key={purchase.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(purchase.purchased_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {purchase.package_size.toLocaleString()} credits
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        ${purchase.price_paid.toFixed(2)} {purchase.currency}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {purchase.notes || 'Stripe payment'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <div className="text-5xl mb-4">🛍️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Purchase History</h3>
              <p className="text-gray-600 mb-6">
                You haven't purchased any credit packages yet.
              </p>
              <button
                onClick={() => setActiveTab('purchase')}
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
              >
                Purchase Credits
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Credits;
